import { FC, useEffect, useMemo, useState } from "react";
import {
  Upload,
  AddTask as AddTaskIcon,
  Email as EmailIcon,
  PictureAsPdf as PictureAsPdfIcon,
  DataSaverOn as DataSaverOnIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import DropDown from "@/components/forms/inputs/dropDown";
import { IDropDownOption } from "@/types/DropDown";

interface CompanyListRowSelectionActionButtonsProps {
  selectedRows: any[];
  onSelectionChange: (rows: any[]) => void;
  onSelectionCleared: () => void;
  handleBatchUploadModalOpen: (rows: any[]) => void;
  handleBatchTodoModalOpen: (rows: any[]) => void;
  handleSendEmailModalOpen: (selectedRows?: any[]) => void;
  handleCreateDocumentModalOpen: (selectedRows?: any[]) => void;
  handleCreateTrackedServiceModalOpen: (selectedRows?: any[]) => void;
}

interface ActionDropDownOption extends IDropDownOption {
  icon?: React.ReactNode;
}

const CompanyListRowSelectionActionButtons: FC<
  CompanyListRowSelectionActionButtonsProps
> = ({
  selectedRows,
  onSelectionChange,
  onSelectionCleared,
  handleBatchUploadModalOpen,
  handleBatchTodoModalOpen,
  handleSendEmailModalOpen,
  handleCreateDocumentModalOpen,
  handleCreateTrackedServiceModalOpen,
}) => {
  const { t } = useTranslation();
  const [resetDropDownSignal, setResetDropDownSignal] = useState<number>(0);

  useEffect(() => {
    onSelectionChange(selectedRows || []);
  }, [selectedRows, onSelectionChange]);

  useEffect(() => {
    return () => {
      onSelectionCleared();
    };
  }, [onSelectionCleared]);

  const dropdownOptions = useMemo(() => {
    const options: ActionDropDownOption[] = [
      {
        value: "send_email",
        label: t("send_email"),
        icon: <EmailIcon fontSize="small" />,
      },
      {
        value: "create_document",
        label: t("create_document"),
        icon: <PictureAsPdfIcon fontSize="small" />,
      },
      {
        value: "upload_file",
        label: t("upload_file"),
        icon: <Upload fontSize="small" />,
      },
      {
        value: "trackedservice_create_short",
        label: t("trackedservice_create_short"),
        icon: <DataSaverOnIcon fontSize="small" />,
      },
      {
        value: "create_todo",
        label: t("create_todo"),
        icon: <AddTaskIcon fontSize="small" />,
      },
    ];

    return options;
  }, [selectedRows, t]);

  const handleDropdownChange = (
    selectedOption: ActionDropDownOption | ActionDropDownOption[] | any,
  ) => {
    const option = selectedOption as ActionDropDownOption;
    if (!option || !option.value) return;

    switch (option.value) {
      case "send_email":
        handleSendEmailModalOpen(selectedRows);
        break;
      case "create_document":
        handleCreateDocumentModalOpen(selectedRows);
        break;
      case "upload_file":
        handleBatchUploadModalOpen(selectedRows);
        break;
      case "create_todo":
        handleBatchTodoModalOpen(selectedRows);
        break;
      case "trackedservice_create_short":
        handleCreateTrackedServiceModalOpen(selectedRows);
        break;
    }

    setResetDropDownSignal((s) => s + 1);
  };

  return (
    <DropDown
      options={dropdownOptions}
      onChange={handleDropdownChange}
      resetSignal={resetDropDownSignal}
      isBatchAction
      placeholder={t("actions")}
      isClearable={false}
      isSearchable={false}
      formatOptionLabel={(option: ActionDropDownOption) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {option.icon}
          {option.label}
        </div>
      )}
      wrapperStyle={{ minWidth: "25vw" }}
      menuPlacement="auto"
    />
  );
};

export default CompanyListRowSelectionActionButtons;
